/**
 * The anchor codec: `ref` string ⇄ `Anchor`, and `Pos` encode/decode.
 * SPEC §3.1, Layers 1 and 2.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS FILE IS A FROZEN CONTRACT. See the banner in `types.ts`.
 *
 * Every function here is PURE. No clock, no randomness, no I/O, no `Date.now()`
 * — a `ref` parsed in 2026 and the same `ref` parsed in 2040 must produce the
 * same `Anchor`, on a device whose locale, timezone and Unicode version are all
 * different.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * THE GRAMMAR
 * ───────────
 *   ref        := quran | external | free
 *   quran      := "hafs:" surah ":" ayah [ range-tail | words-tail ]
 *   range-tail := "-" surah ":" ayah
 *   words-tail := ":" word-start "-" word-end
 *   external   := "ext:" body [ "@" corpus-version ]
 *   free       := "free:"
 *
 *   surah, ayah, word-end : /[1-9][0-9]* /      (no leading zeros, no sign)
 *   word-start            : /0|[1-9][0-9]* /    (0-based, so 0 is legal)
 *
 * Disambiguating a range from a word span is done on the colon count, and it is
 * unambiguous because the range's dash sits in field 2 and the word span's dash
 * sits in field 3:
 *
 *   'hafs:2:255'        → ['hafs','2','255']              3 fields → ayah
 *   'hafs:2:255-2:257'  → ['hafs','2','255-2','257']      4 fields, dash in [2]
 *   'hafs:2:255:8-13'   → ['hafs','2','255','8-13']       4 fields, dash in [3]
 *
 * ROUND-TRIP
 * ──────────
 *   serialiseRef(parseRef(r)) === r        for every canonical r
 *   parseRef(serialiseRef(a)) deep-equals a for every canonical Anchor
 *
 * Both are property-tested in `anchor.spec.ts`. The parser is therefore STRICT:
 * it rejects leading zeros, a leading `+`, surrounding whitespace and an empty
 * corpus version, because each of those would parse to an `Anchor` that
 * serialises back to a DIFFERENT string, and a `ref` that changes shape on a
 * round trip is a `ref` that stops matching the one in the backup file.
 *
 * NORMALISATION THAT IS DELIBERATELY NOT DONE
 * ───────────────────────────────────────────
 * `hafs:2:255-2:255` stays a `range` and is not collapsed to an `ayah`. It is
 * a legal degenerate range, it round-trips, and rewriting a user's `ref` on
 * parse would mean the string in the backup file and the string in the database
 * disagree — which is exactly the class of bug Layer 1 exists to prevent.
 */

import {
	AnchorRefError,
	MAX_POS,
	NO_POS,
	NUMBERING,
	TOTAL_AYAT,
	WORD_SLOTS,
	type Anchor,
	type AnchorSource,
	type AyahId,
	type Pos,
	type Span
} from './types';
import { ayahIdOf, isValidAyahId, isValidLocation, locationOf } from './offsets';
/* The one dependency this module takes on the fixture, and it is taken
 * DELIBERATELY and in one direction only. `codec.ts` used to import nothing
 * from `wordmap.ts`, which sounded like clean layering and in practice meant
 * the "does this word exist" check lived in NO layer: not at mint time, not at
 * parse time, not at import time. The import is acyclic (`wordmap` reads
 * `offsets` and `types`, neither of which reads `codec`) and it is confined to
 * the MINT helpers and `refIsInRange` — `parseRef` still does not consult the
 * fixture, because parsing must stay total over refs from a future anchor
 * space. */
import { isWordSpanInAyah, wordCountOf } from './wordmap';

/** The `ref` of the one and only unanchored form. */
export const FREE_REF = 'free:';

/** Prefix of a Qur'an anchor. Equals `NUMBERING`. */
export const QURAN_PREFIX = `${NUMBERING}:`;

/** Prefix of a corpus anchor. Note it is `ext:`, while the kind is `external`. */
export const EXTERNAL_PREFIX = 'ext:';

/** Longest `ref` accepted. A `ref` is a coordinate, not a payload. */
export const REF_MAX_LENGTH = 256;

const KNOWN_SOURCES: readonly AnchorSource[] = ['azkar', 'dua', 'asma', 'prayer_log', 'fast_log'];

/** 1.. with no leading zeros. */
const POSITIVE_INT = /^[1-9][0-9]*$/;

/** 0.. with no leading zeros. */
const NON_NEGATIVE_INT = /^(?:0|[1-9][0-9]*)$/;

/**
 * Anything an `ext:` body or corpus version may not contain. Dots, dashes,
 * colons and '@' are all legal — corpus ids look like `azkar.morning.0007` and
 * `prayer_log:2026-09-08:fajr`.
 *
 * WHY THE INVISIBLE AND BIDI SET IS HERE, AND WHY NOW
 * ──────────────────────────────────────────────────
 * An `ext:` id lands verbatim in two places: the plain-text backup file the
 * user is meant to be able to open in a text editor in 2040, and the tier-4
 * Repair screen — inside an application whose `<html>` carries `dir="rtl"`
 * permanently (DECISIONS D2). A U+202E RIGHT-TO-LEFT OVERRIDE inside an id
 * visually reorders the rest of the line it sits on; a U+200B ZERO WIDTH SPACE
 * makes two visually identical ids DISTINCT KEYS, so the user sees what looks
 * like one row twice and can never reconcile them.
 *
 * Narrowing this charset AFTER refs are minted is a quarantine event for every
 * ref that stops parsing. Widening it later is free. So it is narrowed now, in
 * M0, before any data exists.
 *
 * The set: whitespace, C0/C1 controls, the soft hyphen U+00AD, the Arabic
 * letter mark U+061C, the zero-width and directional-mark block U+200B..U+200F,
 * the bidi embedding/override controls U+202A..U+202E, the bidi isolates
 * U+2066..U+2069, and the byte order mark U+FEFF.
 */
/* eslint-disable no-control-regex -- rejecting control characters is the point */
const REF_FORBIDDEN =
	/[\s\u0000-\u001F\u007F-\u009F\u00AD\u061C\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/u;
/* eslint-enable no-control-regex */

function fail(code: string, ref: string, message: string): never {
	throw new AnchorRefError(code, ref, message);
}

function parsePositive(field: string, ref: string, what: string): number {
	if (!POSITIVE_INT.test(field)) {
		fail('malformed_number', ref, `${what} must be a positive integer without leading zeros`);
	}
	return Number(field);
}

function parseNonNegative(field: string, ref: string, what: string): number {
	if (!NON_NEGATIVE_INT.test(field)) {
		fail('malformed_number', ref, `${what} must be a non-negative integer without leading zeros`);
	}
	return Number(field);
}

/* ═══════════════════════ Layer 2 — Pos ═══════════════════════════════════ */

/**
 * `Pos = ayahId * WORD_SLOTS + wordIndex`.
 *
 * An ayah anchor spans `posOf(id, 0) .. posOf(id, WORD_SLOTS - 1)`, so word
 * anchors NEST inside ayah anchors and every overlap question is a plain
 * integer-interval test with no special cases (SPEC §3.1).
 */
export function posOf(ayahId: AyahId, wordIndex: number): Pos {
	if (!isValidAyahId(ayahId)) {
		fail('ayah_id_out_of_range', String(ayahId), `ayahId ${ayahId} is not in 1..${TOTAL_AYAT}`);
	}
	if (!Number.isInteger(wordIndex) || wordIndex < 0 || wordIndex >= WORD_SLOTS) {
		fail(
			'word_index_out_of_range',
			String(wordIndex),
			`word index ${wordIndex} is not in 0..${WORD_SLOTS - 1}`
		);
	}
	return ayahId * WORD_SLOTS + wordIndex;
}

/** `true` iff `pos` is a real position (not `NO_POS`, not out of range). */
export function isValidPos(pos: unknown): pos is Pos {
	return typeof pos === 'number' && Number.isInteger(pos) && pos >= WORD_SLOTS && pos <= MAX_POS;
}

/** The ayah a `Pos` falls in. Throws on `NO_POS` and on out-of-range input. */
export function ayahIdOfPos(pos: Pos): AyahId {
	if (!isValidPos(pos)) fail('pos_out_of_range', String(pos), `Pos ${pos} is not a valid position`);
	return Math.floor(pos / WORD_SLOTS);
}

/** The word index a `Pos` names, 0-based. Throws on `NO_POS`. */
export function wordIndexOfPos(pos: Pos): number {
	if (!isValidPos(pos)) fail('pos_out_of_range', String(pos), `Pos ${pos} is not a valid position`);
	return pos % WORD_SLOTS;
}

/* ═══════════════════════ Layer 1 — the ref string ════════════════════════ */

/**
 * Parse a `ref` into an `Anchor`. Throws `AnchorRefError` on anything that is
 * not exactly canonical — see the strictness note in the file header.
 *
 * Only the fields the `ref` actually carries are set. Optional keys are ABSENT,
 * never `undefined`, so `JSON.stringify` of the anchor written to a backup file
 * has no `"word_start": null` in it and deep equality after a round trip is
 * meaningful.
 */
export function parseRef(ref: string): Anchor {
	if (typeof ref !== 'string') fail('not_a_string', String(ref), 'A ref must be a string');
	if (ref.length === 0) fail('empty', ref, 'A ref may not be empty');
	if (ref.length > REF_MAX_LENGTH) {
		fail('too_long', ref, `A ref may not exceed ${REF_MAX_LENGTH} characters`);
	}

	if (ref === FREE_REF) {
		return { kind: 'free', numbering: NUMBERING, ref };
	}
	if (ref.startsWith(EXTERNAL_PREFIX)) return parseExternal(ref);
	if (ref.startsWith(QURAN_PREFIX)) return parseQuran(ref);

	fail(
		'unknown_scheme',
		ref,
		`A ref must start with '${QURAN_PREFIX}', '${EXTERNAL_PREFIX}' or be '${FREE_REF}'`
	);
}

function parseExternal(ref: string): Anchor {
	const rest = ref.slice(EXTERNAL_PREFIX.length);
	if (rest.length === 0) fail('external_empty', ref, 'An ext: ref must name an item');
	if (REF_FORBIDDEN.test(rest)) {
		fail('external_charset', ref, 'An ext: ref may not contain whitespace or control characters');
	}

	// The corpus version is whatever follows the LAST '@'. Item ids may contain
	// '@' themselves; the split is still a round-trip because serialisation
	// rejoins with a single '@' at the same place.
	const at = rest.lastIndexOf('@');
	const body = at === -1 ? rest : rest.slice(0, at);
	const corpusVersion = at === -1 ? undefined : rest.slice(at + 1);

	if (body.length === 0) fail('external_empty', ref, 'An ext: ref must name an item');
	if (corpusVersion !== undefined && corpusVersion.length === 0) {
		fail('external_version_empty', ref, "A trailing '@' with no corpus version is not canonical");
	}

	const anchor: Anchor = {
		kind: 'external',
		numbering: NUMBERING,
		ref,
		source_id: body
	};

	// `source` is DERIVED and lossy on purpose: it is a convenience for queries,
	// it is never used to rebuild the ref, and an unknown corpus simply leaves
	// it absent rather than making the whole ref unparseable. A future corpus
	// therefore imports into an old build instead of quarantining.
	const head = body.slice(0, firstIndexOfAny(body, [':', '.']));
	const known = KNOWN_SOURCES.find((s) => s === head);
	if (known) anchor.source = known;
	if (corpusVersion !== undefined) anchor.corpus_version = corpusVersion;

	return anchor;
}

function firstIndexOfAny(haystack: string, needles: readonly string[]): number {
	let best = haystack.length;
	for (const n of needles) {
		const i = haystack.indexOf(n);
		if (i !== -1 && i < best) best = i;
	}
	return best;
}

function parseQuran(ref: string): Anchor {
	const fields = ref.split(':');

	if (fields.length === 3) {
		const surah = parsePositive(fields[1] as string, ref, 'surah');
		const ayah = parsePositive(fields[2] as string, ref, 'ayah');
		requireLocation(surah, ayah, ref);
		return { kind: 'ayah', numbering: NUMBERING, ref, surah, ayah };
	}

	if (fields.length === 4) {
		const second = fields[2] as string;
		const third = fields[3] as string;
		const rangeDash = second.indexOf('-');

		if (rangeDash !== -1) return parseRange(ref, fields[1] as string, second, third, rangeDash);
		return parseWords(ref, fields[1] as string, second, third);
	}

	fail('malformed_quran_ref', ref, 'A hafs: ref must have 3 or 4 colon-separated fields');
}

function parseRange(
	ref: string,
	surahField: string,
	second: string,
	third: string,
	rangeDash: number
): Anchor {
	if (third.includes('-')) fail('malformed_range', ref, 'A range ref carries exactly one dash');

	const surah = parsePositive(surahField, ref, 'surah');
	const ayah = parsePositive(second.slice(0, rangeDash), ref, 'ayah');
	const endSurah = parsePositive(second.slice(rangeDash + 1), ref, 'end surah');
	const endAyah = parsePositive(third, ref, 'end ayah');

	requireLocation(surah, ayah, ref);
	requireLocation(endSurah, endAyah, ref);

	if (ayahIdOf(endSurah, endAyah) < ayahIdOf(surah, ayah)) {
		fail('range_reversed', ref, 'A range ref may not end before it starts');
	}

	return {
		kind: 'range',
		numbering: NUMBERING,
		ref,
		surah,
		ayah,
		end_surah: endSurah,
		end_ayah: endAyah
	};
}

function parseWords(ref: string, surahField: string, second: string, third: string): Anchor {
	const dash = third.indexOf('-');
	if (dash === -1) fail('malformed_words', ref, 'A word-span ref carries a dash in its last field');
	if (third.indexOf('-', dash + 1) !== -1) {
		fail('malformed_words', ref, 'A word-span ref carries exactly one dash');
	}

	const surah = parsePositive(surahField, ref, 'surah');
	const ayah = parsePositive(second, ref, 'ayah');
	requireLocation(surah, ayah, ref);

	const wordStart = parseNonNegative(third.slice(0, dash), ref, 'word start');
	const wordEnd = parsePositive(third.slice(dash + 1), ref, 'word end');

	if (wordEnd <= wordStart) {
		fail('empty_word_span', ref, 'word_end is EXCLUSIVE and must be greater than word_start');
	}
	if (wordStart >= WORD_SLOTS) {
		fail('word_index_out_of_range', ref, `word_start must be < ${WORD_SLOTS}`);
	}
	if (wordEnd > WORD_SLOTS) {
		fail('word_index_out_of_range', ref, `word_end must be <= ${WORD_SLOTS}`);
	}

	return {
		kind: 'words',
		numbering: NUMBERING,
		ref,
		surah,
		ayah,
		word_start: wordStart,
		word_end: wordEnd
	};
}

function requireLocation(surah: number, ayah: number, ref: string): void {
	if (!isValidLocation(surah, ayah)) {
		fail('ayah_out_of_range', ref, `Ayah ${surah}:${ayah} does not exist in the Hafs count`);
	}
}

/** `parseRef` that reports failure as `null` instead of throwing. */
export function tryParseRef(ref: string): Anchor | null {
	try {
		return parseRef(ref);
	} catch (error) {
		if (error instanceof AnchorRefError) return null;
		throw error;
	}
}

/** `true` iff `ref` is a canonical anchor ref. */
export function isValidRef(ref: string): boolean {
	return tryParseRef(ref) !== null;
}

/**
 * Render an `Anchor` back to its canonical `ref`.
 *
 * This reads the STRUCTURED fields, never `anchor.ref`, so that a row whose
 * derived fields were rebuilt from a newer table still serialises correctly and
 * so that a mismatch between the two is detectable rather than papered over.
 */
export function serialiseRef(anchor: Anchor): string {
	const out = render(anchor);
	// VALIDATE OUR OWN OUTPUT. `requireFields` checks PRESENCE, never validity,
	// so before this line `serialiseRef` could emit `hafs:999:1`,
	// `hafs:2:255:-1-0` and `ext:a b` — none of which re-parse. That matters
	// more here than anywhere else in the codebase: this is the function that
	// writes Layer 1 into the backup file, the single artefact the whole design
	// promises will still be readable in 2040 with `jq` and no copy of the app.
	// A ref that cannot be parsed is not a coordinate, it is a corrupted file.
	//
	// The mint helpers (`rangeRef`, `wordsRef`, `externalRef`) already work this
	// way; `serialiseRef` is the one that did not, and its docstring says it
	// exists precisely for anchors rebuilt from structured fields — which is the
	// population that breaks.
	parseRef(out);
	return out;
}

function render(anchor: Anchor): string {
	switch (anchor.kind) {
		case 'free':
			return FREE_REF;

		case 'external': {
			const id = anchor.source_id;
			if (!id) {
				fail('external_empty', anchor.ref, 'An external anchor must carry a source_id');
			}
			return anchor.corpus_version
				? `${EXTERNAL_PREFIX}${id}@${anchor.corpus_version}`
				: `${EXTERNAL_PREFIX}${id}`;
		}

		case 'ayah':
			requireFields(anchor, ['surah', 'ayah']);
			return `${QURAN_PREFIX}${anchor.surah}:${anchor.ayah}`;

		case 'range':
			requireFields(anchor, ['surah', 'ayah', 'end_surah', 'end_ayah']);
			return `${QURAN_PREFIX}${anchor.surah}:${anchor.ayah}-${anchor.end_surah}:${anchor.end_ayah}`;

		case 'words':
			requireFields(anchor, ['surah', 'ayah', 'word_start', 'word_end']);
			return `${QURAN_PREFIX}${anchor.surah}:${anchor.ayah}:${anchor.word_start}-${anchor.word_end}`;
	}
}

function requireFields(anchor: Anchor, fields: readonly (keyof Anchor)[]): void {
	for (const field of fields) {
		if (anchor[field] === undefined) {
			fail('missing_field', anchor.ref, `A '${anchor.kind}' anchor must carry ${String(field)}`);
		}
	}
}

/* ═══════════════════ Layer 1 → Layer 2 — the span ════════════════════════ */

/**
 * The indexed span of an anchor, both ends INCLUSIVE.
 *
 *   ayah      posOf(id, 0) .. posOf(id, WORD_SLOTS - 1)
 *   range     posOf(startId, 0) .. posOf(endId, WORD_SLOTS - 1)
 *   words     posOf(id, word_start) .. posOf(id, word_end - 1)
 *   external  NO_POS .. NO_POS
 *   free      NO_POS .. NO_POS
 *
 * SPEC §3.1's worked example: `hafs:2:255:8-13` on ayahId 262 gives
 * `start_pos = 268296`, `end_pos = 268300`.
 */
export function spanOf(anchor: Anchor): Span {
	switch (anchor.kind) {
		case 'free':
		case 'external':
			return { start_pos: NO_POS, end_pos: NO_POS };

		case 'ayah': {
			const id = ayahIdOfAnchor(anchor);
			return { start_pos: posOf(id, 0), end_pos: posOf(id, WORD_SLOTS - 1) };
		}

		case 'range': {
			const startId = ayahIdOfAnchor(anchor);
			const endId = ayahIdOf(anchor.end_surah as number, anchor.end_ayah as number);
			return { start_pos: posOf(startId, 0), end_pos: posOf(endId, WORD_SLOTS - 1) };
		}

		case 'words': {
			const id = ayahIdOfAnchor(anchor);
			return {
				start_pos: posOf(id, anchor.word_start as number),
				end_pos: posOf(id, (anchor.word_end as number) - 1)
			};
		}
	}
}

/** The ayahId an anchor starts at. Throws for `free` and `external`. */
export function ayahIdOfAnchor(anchor: Anchor): AyahId {
	if (anchor.kind === 'free' || anchor.kind === 'external') {
		fail('no_position', anchor.ref, `A '${anchor.kind}' anchor has no place in the mushaf`);
	}
	return ayahIdOf(anchor.surah as number, anchor.ayah as number);
}

/** The ayahId an anchor ends at, inclusive. Throws for `free` and `external`. */
export function endAyahIdOfAnchor(anchor: Anchor): AyahId {
	if (anchor.kind === 'range') {
		return ayahIdOf(anchor.end_surah as number, anchor.end_ayah as number);
	}
	return ayahIdOfAnchor(anchor);
}

/** Number of ayat an anchor touches. 1 for everything but a range. */
export function ayahCountOfAnchor(anchor: Anchor): number {
	if (anchor.kind === 'free' || anchor.kind === 'external') return 0;
	return endAyahIdOfAnchor(anchor) - ayahIdOfAnchor(anchor) + 1;
}

/* ═══════════════════ integer-interval helpers ════════════════════════════ */

/** `true` iff both ends of `inner` fall inside `outer`. Nesting, not overlap. */
export function contains(outer: Span, inner: Span): boolean {
	if (outer.start_pos === NO_POS || inner.start_pos === NO_POS) return false;
	return inner.start_pos >= outer.start_pos && inner.end_pos <= outer.end_pos;
}

/** `true` iff the two closed intervals share at least one `Pos`. */
export function overlaps(a: Span, b: Span): boolean {
	if (a.start_pos === NO_POS || b.start_pos === NO_POS) return false;
	return a.start_pos <= b.end_pos && b.start_pos <= a.end_pos;
}

/**
 * Width of a span in `Pos` units. 0 when unplaced.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * `meta.max_span` IS MAINTAINED OVER TWO BUCKETS, NOT ONE. READ THIS BEFORE
 * WIRING THE GUTTER QUERY (SPEC §3.1).
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * SPEC §3.1 drives the per-page gutter query from one number:
 *
 *     db.notes.where('end_pos').between(firstPos, lastPos + maxSpan, true, true)
 *
 * and sells it as O(result) rather than O(table). That holds only while
 * `maxSpan` is SMALL. A word or ayah anchor is bounded: an ayah anchor is
 * exactly `WORD_SLOTS - 1` = 1023 wide, and a word anchor is narrower. A RANGE
 * anchor is unbounded — `spanWidth(spanOf(parseRef('hafs:1:1-114:6')))` is
 * 6,385,663, the whole mushaf.
 *
 * One whole-mushaf range note is trivially creatable and a plausible thing for
 * a user to do exactly once. Folded into a single `max_span`, it permanently
 * turns every page turn into a full-table scan and the property the spec sells
 * is gone — silently, with no error, forever.
 *
 * So `meta.max_span` covers `words` and `ayah` anchors ONLY (ceiling: 1023, and
 * `MAX_BOUNDED_SPAN_WIDTH` below is that ceiling), and range anchors are
 * maintained in a SECOND small number, `meta.max_range_span`, scanned
 * independently. Range notes are rare and few, so the second scan is cheap and
 * the common path stays bounded. `spanBucketOf` is the classifier; it is here,
 * beside `spanWidth`, because this is where a future reader looks for the
 * invariant.
 */
export function spanWidth(span: Span): number {
	if (span.start_pos === NO_POS) return 0;
	return span.end_pos - span.start_pos;
}

/** Which `meta` counter an anchor's width belongs to. */
export type SpanBucket = 'bounded' | 'range' | 'unplaced';

/** The widest a `bounded` span can ever be: one whole ayah. */
export const MAX_BOUNDED_SPAN_WIDTH = WORD_SLOTS - 1;

/** Classify an anchor for `meta.max_span` / `meta.max_range_span`. */
export function spanBucketOf(anchor: Anchor): SpanBucket {
	switch (anchor.kind) {
		case 'free':
		case 'external':
			return 'unplaced';
		case 'range':
			return 'range';
		case 'ayah':
		case 'words':
			return 'bounded';
	}
}

/* ═══════════════════ minting ═════════════════════════════════════════════ */

/** Build the canonical `ref` for a whole ayah. */
export function ayahRef(surah: number, ayah: number): string {
	requireLocation(surah, ayah, `${surah}:${ayah}`);
	return `${QURAN_PREFIX}${surah}:${ayah}`;
}

/** Build the canonical `ref` for an ayah range, inclusive at both ends. */
export function rangeRef(surah: number, ayah: number, endSurah: number, endAyah: number): string {
	const ref = `${QURAN_PREFIX}${surah}:${ayah}-${endSurah}:${endAyah}`;
	// Parse rather than trust: this is the only place a caller's four loose
	// integers become a durable coordinate, so it is the right place to reject.
	parseRef(ref);
	return ref;
}

/**
 * Build the canonical `ref` for a word span. `wordEnd` is EXCLUSIVE.
 *
 * Rejects a span that runs past the ayah's ACTUAL canonical word count, not
 * merely past `WORD_SLOTS`. Before this check existed, `wordsRef(112, 1, 0, 900)`
 * minted a durable, exportable, never-flagged coordinate into an ayah of four
 * words: `parseRef` bounds against 1024 (and must, so a ref from a newer anchor
 * space quarantines rather than being destroyed), and no other layer looked.
 * MINT TIME is the one place a hard reject is correct.
 */
export function wordsRef(surah: number, ayah: number, wordStart: number, wordEnd: number): string {
	const ref = `${QURAN_PREFIX}${surah}:${ayah}:${wordStart}-${wordEnd}`;
	parseRef(ref);
	if (!isWordSpanInAyah(surah, ayah, wordStart, wordEnd)) {
		fail(
			'word_span_not_in_ayah',
			ref,
			`${surah}:${ayah} has ${wordCountOf(ayahIdOf(surah, ayah))} canonical words; ` +
				`[${wordStart}, ${wordEnd}) does not fit`
		);
	}
	return ref;
}

/**
 * Build the canonical `ref` for a corpus item.
 *
 * `'@'` is REJECTED in `sourceId`: the version separator belongs to the codec,
 * not to the caller. `externalRef('azkar@home')` used to mint `ext:azkar@home`,
 * which round-trips as a STRING and so passed the property test, while parsing
 * back with `source_id: 'azkar'` and `corpus_version: 'home'` — the caller's
 * identifier silently split in two at mint time. The parse side stays as it is
 * (last `'@'` wins), because an already-minted ref must keep meaning what it
 * meant.
 */
export function externalRef(sourceId: string, corpusVersion?: string): string {
	if (sourceId.includes('@')) {
		fail(
			'external_id_has_at',
			sourceId,
			"'@' separates an ext: id from its corpus version and may not appear in the id itself"
		);
	}
	const ref = corpusVersion
		? `${EXTERNAL_PREFIX}${sourceId}@${corpusVersion}`
		: `${EXTERNAL_PREFIX}${sourceId}`;
	parseRef(ref);
	return ref;
}

/**
 * `true` iff every word `ref` addresses exists in the frozen fixture.
 *
 * NOT a parse check and deliberately NOT wired into `parseRef`. A ref minted
 * under a NEWER anchor space, or against a corpus this build does not have,
 * must QUARANTINE (tier 4: shown on the Repair screen with its quote, never
 * deleted, never silently rewritten) — not fail to parse. This is the predicate
 * the import path (§3.7 step 6) calls to set `drift` and `quarantine`.
 *
 * Non-`words` anchors are always in range: they carry no word index.
 */
export function refIsInRange(ref: string): boolean {
	const anchor = tryParseRef(ref);
	if (!anchor) return false;
	if (anchor.kind !== 'words') return true;
	return isWordSpanInAyah(
		anchor.surah as number,
		anchor.ayah as number,
		anchor.word_start as number,
		anchor.word_end as number
	);
}

/** The `Location` an anchor starts at. */
export function locationOfAnchor(anchor: Anchor) {
	return locationOf(ayahIdOfAnchor(anchor));
}
